import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AuthModal from '../../components/feature/AuthModal';
import QuoteModal from '../../components/feature/QuoteModal';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface Product {
  id: string;
  name: string;
  slug: string;
  category: string;
  description: string;
  short_description: string;
  price: number | null;
  image_url: string;
  is_featured: boolean;
  created_at: string;
}

const ProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    loadProduct();
    checkAuth();
  }, [slug]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      
      // Récupérer le produit actuel
      const { data: productData, error: productError } = await supabase
        .from('products')
        .select('*')
        .eq('slug', slug)
        .single();

      if (productError) throw productError;
      
      setProduct(productData);

      // Récupérer les produits similaires
      if (productData) {
        const { data: relatedData, error: relatedError } = await supabase
          .from('products')
          .select('*')
          .eq('category', productData.category)
          .neq('id', productData.id)
          .limit(4);

        if (relatedError) throw relatedError;
        setRelatedProducts(relatedData || []);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setIsAuthenticated(!!user);
  };

  const handleQuoteRequest = () => {
    if (isAuthenticated) {
      setShowQuoteModal(true);
    } else {
      setShowAuthModal(true);
    }
  };

  const handleAuthSuccess = () => {
    setIsAuthenticated(true);
    setShowQuoteModal(true);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-loader-4-line animate-spin text-5xl text-[#E31B23] mb-4"></i>
            <p className="text-lg text-[#666666]">Chargement du produit...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-[#1A1A1A] mb-4">Produit non trouvé</h2>
            <Link to="/products" className="text-[#E31B23] hover:underline cursor-pointer">
              Retour aux produits
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const images = [product.image_url, product.image_url, product.image_url, product.image_url, product.image_url];

  const tabs = [
    { id: 'description', label: 'Description complète', icon: 'ri-file-text-line' },
    { id: 'specifications', label: 'Caractéristiques techniques', icon: 'ri-settings-3-line' },
    { id: 'certifications', label: 'Certifications', icon: 'ri-award-line' },
    { id: 'documentation', label: 'Documentation', icon: 'ri-file-download-line' },
  ];

  return (
    <>
      <header>
        <h1 className="sr-only">{product.name} - ADF Biomédical</h1>
      </header>
      <Navbar />
      <main>
        <section className="py-32 bg-[#F8F8F8]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <p className="text-sm text-[#666666]">
                <Link to="/" className="hover:text-[#E31B23] transition-colors cursor-pointer">Accueil</Link>
                {' / '}
                <Link to="/products" className="hover:text-[#E31B23] transition-colors cursor-pointer">Produits</Link>
                {' / '}
                <span className="text-[#1A1A1A]">{product.name}</span>
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg mb-6">
                  <div className="w-full h-96 mb-6 rounded-xl overflow-hidden">
                    <img
                      src={images[selectedImage]}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex gap-3 overflow-x-auto">
                    {images.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
                          selectedImage === index ? 'border-[#E31B23]' : 'border-gray-200'
                        }`}
                      >
                        <img
                          src={img}
                          alt={`${product.name} ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-4xl font-bold text-[#1A1A1A] mb-4">{product.name}</h2>
                  <p className="text-base text-[#E31B23] font-medium mb-6">{product.category}</p>

                  {product.is_featured && (
                    <div className="flex gap-3 mb-6">
                      <span className="px-4 py-2 bg-[#E31B23]/10 text-[#E31B23] rounded-full text-sm font-medium">
                        Produit en vedette
                      </span>
                    </div>
                  )}

                  <p className="text-lg text-[#666666] leading-relaxed mb-8">
                    {product.description}
                  </p>

                  <button
                    onClick={handleQuoteRequest}
                    className="w-full bg-[#E31B23] text-white py-4 rounded-lg font-medium hover:bg-[#C41820] transition-colors flex items-center justify-center gap-2 mb-6 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-file-list-3-line text-xl"></i>
                    Demander un devis
                  </button>

                  <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-200">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-[#E31B23]/10 rounded-full flex items-center justify-center mx-auto mb-2">
                        <i className="ri-shield-check-line text-xl text-[#E31B23]"></i>
                      </div>
                      <p className="text-xs text-gray-600">Produits certifiés et conformes</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-[#E31B23]/10 rounded-full flex items-center justify-center mx-auto mb-2">
                        <i className="ri-truck-line text-xl text-[#E31B23]"></i>
                      </div>
                      <p className="text-xs text-gray-600">Livraison dans toute l'Algérie</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-[#E31B23]/10 rounded-full flex items-center justify-center mx-auto mb-2">
                        <i className="ri-customer-service-2-line text-xl text-[#E31B23]"></i>
                      </div>
                      <p className="text-xs text-gray-600">Support technique expert</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-white rounded-2xl shadow-lg overflow-hidden"
            >
              <div className="border-b border-gray-200">
                <div className="flex overflow-x-auto">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center px-8 py-5 text-base font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'text-[#E31B23] border-b-4 border-[#E31B23]'
                          : 'text-[#666666] hover:text-[#E31B23]'
                      }`}
                    >
                      <i className={`${tab.icon} text-xl mr-3`}></i>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-10">
                {activeTab === 'description' && (
                  <div>
                    <h3 className="text-2xl font-bold text-[#1A1A1A] mb-6">Description complète</h3>
                    <p className="text-base text-[#666666] leading-relaxed">
                      {product.description}
                    </p>
                  </div>
                )}

                {activeTab === 'specifications' && (
                  <div>
                    <h3 className="text-2xl font-bold text-[#1A1A1A] mb-6">Caractéristiques techniques</h3>
                    <p className="text-base text-[#666666]">Caractéristiques techniques disponibles sur demande.</p>
                  </div>
                )}

                {activeTab === 'certifications' && (
                  <div>
                    <h3 className="text-2xl font-bold text-[#1A1A1A] mb-6">Certifications</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-[#F8F8F8] rounded-xl p-6 text-center">
                        <i className="ri-award-line text-5xl text-[#E31B23] mb-4"></i>
                        <h4 className="text-xl font-bold text-[#1A1A1A]">CE</h4>
                      </div>
                      <div className="bg-[#F8F8F8] rounded-xl p-6 text-center">
                        <i className="ri-award-line text-5xl text-[#E31B23] mb-4"></i>
                        <h4 className="text-xl font-bold text-[#1A1A1A]">ISO 13485</h4>
                      </div>
                      <div className="bg-[#F8F8F8] rounded-xl p-6 text-center">
                        <i className="ri-award-line text-5xl text-[#E31B23] mb-4"></i>
                        <h4 className="text-xl font-bold text-[#1A1A1A]">FDA</h4>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'documentation' && (
                  <div>
                    <h3 className="text-2xl font-bold text-[#1A1A1A] mb-6">Documentation</h3>
                    <div className="space-y-4">
                      <a
                        href="#"
                        className="flex items-center justify-between p-6 bg-[#F8F8F8] rounded-xl hover:bg-gray-200 transition-colors duration-300 cursor-pointer"
                      >
                        <div className="flex items-center">
                          <i className="ri-file-pdf-line text-4xl text-[#E31B23] mr-4"></i>
                          <div>
                            <h4 className="text-lg font-semibold text-[#1A1A1A]">Fiche technique</h4>
                            <p className="text-sm text-[#666666]">Documentation complète du produit</p>
                          </div>
                        </div>
                        <i className="ri-download-line text-2xl text-[#E31B23]"></i>
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </section>

        {relatedProducts.length > 0 && (
          <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="text-center mb-16"
              >
                <h3 className="text-4xl font-bold text-[#1A1A1A] mb-6">Produits similaires</h3>
                <p className="text-lg text-[#666666]">Découvrez d'autres produits de la même catégorie</p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-product-shop>
                {relatedProducts.map((relatedProduct, index) => (
                  <motion.div
                    key={relatedProduct.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 group"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={relatedProduct.image_url}
                        alt={relatedProduct.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    <div className="p-6">
                      <h4 className="text-lg font-semibold text-[#1A1A1A] mb-3 line-clamp-2">
                        {relatedProduct.name}
                      </h4>
                      <Link
                        to={`/products/${relatedProduct.slug}`}
                        className="inline-block w-full text-center px-6 py-2 border-2 border-[#E31B23] text-[#E31B23] rounded-lg font-medium hover:bg-[#E31B23] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
                      >
                        Voir les détails
                      </Link>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-24 bg-[#F8F8F8]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="text-4xl font-bold text-[#1A1A1A] mb-6">Besoin d'informations complémentaires ?</h3>
              <p className="text-lg text-[#666666] mb-8 max-w-2xl mx-auto">
                Notre équipe technique est à votre disposition pour répondre à toutes vos questions
              </p>
              <Link
                to="/contact"
                className="inline-block px-10 py-4 bg-[#E31B23] text-white rounded-lg font-medium text-lg hover:bg-[#C41820] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Contactez-nous
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
      <QuoteModal
        isOpen={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
        productId={product?.id || ''}
        productName={product?.name || ''}
      />
    </>
  );
};

export default ProductDetailPage;
